import { CollapsibleMenuItem } from 'modules/trainings/components/content/Consumer'
import { contentRegistry } from 'modules/trainings/components/content/ContentRegistry'
import { Block } from 'modules/trainings/lib/content/Block'
import { ModuleCollection } from 'modules/trainings/lib/content/ModuleCollection'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'

export interface BuildCollapsibleMenuItemsOptions {
  /** Filter out QuizBlocks that don't have a saved form (no form ID) */
  filterEmptyQuizBlocks?: boolean
}

/**
 * Builds menu items for the F0CollapsibleMenu component from a ModuleCollection.
 *
 * Accessibility rules:
 * - First module is always accessible
 * - A module is accessible only if all previous modules are completed
 * - A block is accessible if:
 *   - Its module is accessible AND
 *   - (Block is completed OR is the currently selected item OR is the first non-completed block)
 */
export function buildCollapsibleMenuItems(
  collection: ModuleCollection,
  options: BuildCollapsibleMenuItemsOptions = {}
): CollapsibleMenuItem[] {
  const { filterEmptyQuizBlocks = false } = options

  const filterBlock = (block: Block): boolean => {
    if (!filterEmptyQuizBlocks) return true
    // Filter out QuizBlocks that don't have a saved form (no ID) or have no questions
    return !(block instanceof QuizBlock && !block.form.id)
  }

  const allBlocks = collection.modules.flatMap((m) => m.blocks.filter(filterBlock))
  const firstNonCompletedUuid = allBlocks.find((b) => b.status !== 'completed')?.uuid

  return collection.modules.map((module, moduleIndex) => {
    const isPreviousModulesCompleted = collection.modules
      .slice(0, moduleIndex)
      .every((m) => m.blocks.every((b) => b.status === 'completed'))

    const isModuleAccessible = moduleIndex === 0 || isPreviousModulesCompleted

    return {
      id: module.uuid,
      label: module.title,
      icon: contentRegistry.fetch(module, 'icon'),
      disabled: !isModuleAccessible,
      onClick: () => isModuleAccessible && collection.setSelected(module.uuid),
      children: module.blocks.filter(filterBlock).map((block) => {
        const isAccessible =
          isModuleAccessible &&
          (block.status === 'completed' ||
            block.uuid === collection.selectedItem?.uuid ||
            block.uuid === firstNonCompletedUuid)

        return {
          id: block.uuid,
          label: block.title,
          icon: contentRegistry.fetch(block, 'icon'),
          disabled: !isAccessible,
          onClick: () => isAccessible && collection.setSelected(block.uuid),
        }
      }),
    }
  })
}
